import React, { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './PaymentPorterPage.css';
import './ProfileIATAPage.css';
import pmmService from '../services/paymentMigrationManagerService';

const statusColour = (s) => {
    if (s === 'SUCCESS') return '#16a34a';
    if (s === 'ERROR')   return '#dc2626';
    if (s === 'SKIPPED') return '#d97706';
    return '#6b7280';
};

const PaymentMigrationManagerRunPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const today = new Date().toISOString().split('T')[0];
    const [startDate, setStartDate] = useState(today);
    const [endDate,   setEndDate]   = useState(today);
    const [dateType,  setDateType]  = useState('arrival');

    const [preview, setPreview]       = useState(null);
    const [previewing, setPreviewing] = useState(false);
    const [previewError, setPreviewError] = useState(null);

    const [running,    setRunning]    = useState(false);
    const [runRef,     setRunRef]     = useState(null);
    const [results,    setResults]    = useState([]);
    const [runError,   setRunError]   = useState(null);
    const [runComplete, setRunComplete] = useState(false);
    const [runningId,  setRunningId]  = useState(null);

    const handlePreview = useCallback(async () => {
        setPreviewing(true);
        setPreview(null);
        setPreviewError(null);
        setResults([]);
        setRunComplete(false);
        setRunRef(null);
        try {
            const res = await pmmService.preview(id, startDate, endDate, dateType, true);
            setPreview(res.data);
        } catch (err) {
            console.error('Preview failed', err);
            setPreviewError('Failed to fetch preview from OHIP');
        } finally {
            setPreviewing(false);
        }
    }, [id, startDate, endDate, dateType]);

    const runSingle = useCallback(async (reservation, ref) => {
        setRunningId(reservation.reservationId);
        try {
            const res = await pmmService.run(id, {
                reservationId:      reservation.reservationId,
                confirmationNumber: reservation.confirmationNum,
                startDate,
                endDate,
                dateType,
                matchedCriteria:    reservation.matchedCriteria,
                runRef:             ref,
            });
            const result = res.data;
            setResults(prev => [...prev, { reservationId: reservation.reservationId, confirmationNum: reservation.confirmationNum, status: result.status, skipReason: result.skipReason, actionsApplied: result.actionsApplied }]);
        } catch (err) {
            console.error('Run failed for', reservation.reservationId, err);
            const errMsg = err.response?.data?.message || err.message || 'Unknown error';
            setResults(prev => [...prev, { reservationId: reservation.reservationId, confirmationNum: reservation.confirmationNum, status: 'ERROR', error: errMsg }]);
        } finally {
            setRunningId(null);
        }
    }, [id, startDate, endDate, dateType]);

    const handleRunSingle = useCallback(async (reservation) => {
        let ref = runRef;
        if (!ref) {
            const refRes = await pmmService.runRef(id);
            ref = refRes.data?.runRef;
            setRunRef(ref);
        }
        await runSingle(reservation, ref);
    }, [id, runRef, runSingle]);

    const handleRunAll = useCallback(async () => {
        if (!preview?.matchedReservations?.length) return;
        if (!window.confirm(`Apply actions to all ${preview.matchedReservations.length} matching reservations?`)) return;

        setRunning(true);
        setRunError(null);
        setResults([]);
        setRunComplete(false);

        try {
            const refRes = await pmmService.runRef(id);
            const ref = refRes.data?.runRef;
            setRunRef(ref);

            for (const reservation of preview.matchedReservations) {
                await runSingle(reservation, ref);
            }
            setRunComplete(true);
        } catch (err) {
            console.error('Run all failed', err);
            setRunError('Run interrupted: ' + (err.message || 'Unknown error'));
        } finally {
            setRunning(false);
        }
    }, [id, preview, runSingle]);

    const reservations = preview?.matchedReservations || [];
    const processedIds = new Set(results.map(r => r.reservationId));

    return (
        <div className="pi-page">
            <div className="pi-header">
                <div className="pi-header-left">
                    <button className="pi-back-btn" onClick={() => navigate(`/apps/payment-migration-manager/${id}`)}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path d="M10 3L5 8l5 5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Back
                    </button>
                    <div className="pi-title-block">
                        <h1 className="pi-title">Run Payment Migration</h1>
                        <p className="pi-subtitle">Preview matching reservations and apply payment actions</p>
                    </div>
                </div>
            </div>

            {/* Date range picker */}
            <div className="pi-list-panel" style={{padding: 20, marginBottom: 16}}>
                <div style={{display:'flex', gap: 16, alignItems:'flex-end', flexWrap:'wrap'}}>
                    <div className="pi-form-group" style={{margin:0}}>
                        <label className="pi-form-label">Date Type</label>
                        <select className="pi-form-input" value={dateType} onChange={e => setDateType(e.target.value)} style={{width: 150}}>
                            <option value="arrival">Arrival</option>
                            <option value="departure">Departure</option>
                        </select>
                    </div>
                    <div className="pi-form-group" style={{margin:0}}>
                        <label className="pi-form-label">Start Date</label>
                        <input type="date" className="pi-form-input" value={startDate} onChange={e => setStartDate(e.target.value)} />
                    </div>
                    <div className="pi-form-group" style={{margin:0}}>
                        <label className="pi-form-label">End Date</label>
                        <input type="date" className="pi-form-input" value={endDate} onChange={e => setEndDate(e.target.value)} />
                    </div>
                    <button className="pi-btn-add" onClick={handlePreview} disabled={previewing || running} style={{marginBottom: 0}}>
                        {previewing ? 'Loading…' : 'Preview Reservations'}
                    </button>
                </div>
            </div>

            {previewError && (
                <div style={{color:'#dc2626', padding: '12px 20px', background:'#fef2f2', borderRadius: 8, marginBottom: 16}}>{previewError}</div>
            )}

            {preview && (
                <div className="pi-list-panel" style={{padding: 20}}>
                    {/* Summary */}
                    <div style={{display:'flex', gap: 24, marginBottom: 20, flexWrap:'wrap'}}>
                        <div style={{padding:'12px 20px', background:'#f9fafb', borderRadius: 8, textAlign:'center'}}>
                            <div style={{fontSize: 24, fontWeight: 700}}>{preview.total ?? '—'}</div>
                            <div style={{fontSize: 12, color:'#6b7280'}}>Total reservations in range</div>
                        </div>
                        {preview.hasCriteria && (
                            <div style={{padding:'12px 20px', background:'#f0fdf4', borderRadius: 8, textAlign:'center'}}>
                                <div style={{fontSize: 24, fontWeight: 700, color:'#16a34a'}}>{preview.matched ?? '—'}</div>
                                <div style={{fontSize: 12, color:'#6b7280'}}>Matching configured criteria</div>
                            </div>
                        )}
                        {!preview.hasCriteria && (
                            <div style={{padding:'12px 20px', background:'#fffbeb', borderRadius: 8}}>
                                <strong>No criteria configured.</strong> Add corporate profiles, IATA profiles, rate codes or payment methods to filter reservations.
                            </div>
                        )}
                    </div>

                    {reservations.length > 0 && (
                        <>
                            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 12}}>
                                <h3 style={{margin:0, fontSize:15}}>Matching Reservations ({reservations.length})</h3>
                                <button
                                    className="pi-btn-add"
                                    onClick={handleRunAll}
                                    disabled={running || runComplete}
                                >
                                    {running ? 'Running…' : runComplete ? 'Run Complete' : `Run All (${reservations.length})`}
                                </button>
                            </div>

                            {runError && <div style={{color:'#dc2626', marginBottom:12}}>{runError}</div>}

                            <table className="pi-table">
                                <thead>
                                    <tr>
                                        <th>Reservation ID</th>
                                        <th>Confirmation</th>
                                        <th>Guest</th>
                                        <th>Arrival</th>
                                        <th>Departure</th>
                                        <th>Rate</th>
                                        <th>Matched Criteria</th>
                                        <th>Result</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reservations.map(r => {
                                        const result = results.find(x => x.reservationId === r.reservationId);
                                        const isRunning = runningId === r.reservationId;
                                        return (
                                            <tr key={r.reservationId}>
                                                <td style={{fontFamily:'monospace', fontSize:12}}>{r.reservationId}</td>
                                                <td>{r.confirmationNum || '—'}</td>
                                                <td>{r.guestName || '—'}</td>
                                                <td>{r.arrivalDate || '—'}</td>
                                                <td>{r.departureDate || '—'}</td>
                                                <td>{r.ratePlanCode || '—'}</td>
                                                <td style={{fontSize:11,maxWidth:160}}>
                                                    {(r.matchedCriteria || []).join(', ') || '—'}
                                                </td>
                                                <td>
                                                    {result ? (
                                                        <span style={{color: statusColour(result.status), fontWeight: 600}}>
                                                            {result.status}
                                                            {result.skipReason && <div style={{fontSize:11,fontWeight:400,color:'#6b7280'}}>{result.skipReason}</div>}
                                                            {result.error && <div style={{fontSize:11,fontWeight:400,color:'#dc2626'}}>{result.error}</div>}
                                                        </span>
                                                    ) : isRunning ? (
                                                        <span style={{color:'#6b7280'}}>Running…</span>
                                                    ) : '—'}
                                                </td>
                                                <td>
                                                    <button
                                                        className="pi-action-btn"
                                                        onClick={() => handleRunSingle(r)}
                                                        disabled={isRunning || running || processedIds.has(r.reservationId)}
                                                    >
                                                        {processedIds.has(r.reservationId) ? 'Done' : 'Run'}
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </>
                    )}

                    {reservations.length === 0 && preview.hasCriteria && (
                        <div className="pi-empty-title">No matching reservations found for this date range.</div>
                    )}
                </div>
            )}
        </div>
    );
};

export default PaymentMigrationManagerRunPage;
